import React, { useState } from 'react';
import { X, Smartphone, ExternalLink, Apple, Monitor, Download, ChevronDown, Info, CheckCircle2 } from 'lucide-react';
import { Capacitor } from '@capacitor/core';
import { useTranslation } from 'react-i18next';
import { AppUser } from '../types';

interface InstallAppModalProps {
    user?: AppUser | null;
    onClose: () => void;
}

const InstallAppModal: React.FC<InstallAppModalProps> = ({ user, onClose }) => {
    const { t } = useTranslation();
    const platform = Capacitor.getPlatform();
    const isNative = Capacitor.isNativePlatform();

    // Detect actual device OS from user agent when running in web browser
    const ua = navigator.userAgent;
    const isIOSDevice = /iPhone|iPad|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    const isAndroidDevice = /Android/.test(ua);
    const isDesktop = !isIOSDevice && !isAndroidDevice;

    const [showPwaInfo, setShowPwaInfo] = useState(false);
    const [pwaInstalled, setPwaInstalled] = useState(false);

    const hasPwaPrompt = !!(window as any).__pwaInstallPrompt;

    const handlePwaInstall = async () => {
        const prompt = (window as any).__pwaInstallPrompt;
        if (!prompt) return;
        prompt.prompt();
        const { outcome } = await prompt.userChoice;
        if (outcome === 'accepted') {
            (window as any).__pwaInstallPrompt = null;
            setPwaInstalled(true);
        }
    };

    return (
        <div className="fixed left-0 right-0 bottom-0 z-[10020] flex items-center justify-center px-4 pb-4" style={{ top: 'env(safe-area-inset-top, 0px)' }}>
            <div className="absolute inset-0 bg-black/80 backdrop-blur-md" onClick={onClose} />
            <div className="relative w-full max-w-md bg-[var(--bg-base)] border border-[var(--border-secondary)] rounded-panel shadow-3xl overflow-hidden flex flex-col animate-in zoom-in-95 duration-300">

                <div className="px-5 pt-5 pb-4 flex justify-between items-center">
                    <h2 className="text-lg font-black text-[var(--text-primary)] uppercase tracking-tight">{t('install_app.title')}</h2>
                    <button onClick={onClose} className="p-2 text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors bg-[var(--bg-surface-alt)] rounded-xl">
                        <X size={18} />
                    </button>
                </div>

                <div className="px-5 pb-5 space-y-3">

                    {/* Android — hide on native android, and hide on iOS devices in web browser */}
                    {platform !== 'android' && !(platform === 'web' && isIOSDevice) && (
                        <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-2xl p-4 space-y-3">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center text-emerald-400 shrink-0">
                                    <Smartphone size={18} />
                                </div>
                                <div className="min-w-0 flex-1">
                                    <p className="text-[12px] font-black text-[var(--text-primary)] uppercase tracking-tight">Android</p>
                                    <p className="text-2xs text-[var(--text-muted)]">{t('install_app.android_subtitle')}</p>
                                </div>
                            </div>
                            <div className="flex gap-2">
                                <button
                                    type="button"
                                    onClick={() => window.open('https://play.google.com/store/apps/details?id=com.takleconsulting.fricamp', '_blank')}
                                    className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white py-2.5 rounded-xl text-3xs font-black uppercase tracking-widest flex items-center justify-center gap-1.5 shadow-lg shadow-emerald-900/20 transition-all active:scale-95"
                                >
                                    {t('install_app.android_download')}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => window.open('https://groups.google.com/g/fricamp-testere', '_blank')}
                                    className="flex-1 bg-[var(--bg-surface)] hover:bg-[var(--bg-surface-alt)] border border-[var(--border-secondary)] py-2.5 rounded-xl text-3xs font-black uppercase tracking-widest flex items-center justify-center gap-1.5 text-[var(--text-primary)] transition-all active:scale-95"
                                >
                                    {t('install_app.android_join_group')}
                                </button>
                            </div>
                        </div>
                    )}

                    {/* iOS — hide on native ios, and hide on Android devices in web browser */}
                    {platform !== 'ios' && !(platform === 'web' && isAndroidDevice) && (
                        <button
                            type="button"
                            onClick={() => window.open('https://apps.apple.com/no/app/fricamp/id6759742331', '_blank')}
                            className="w-full bg-blue-500/5 hover:bg-blue-500/10 border border-blue-500/20 rounded-2xl p-4 flex items-center gap-3 transition-all active:scale-[0.98]"
                        >
                            <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center text-blue-400 shrink-0">
                                <Apple size={18} />
                            </div>
                            <div className="min-w-0 flex-1 text-left">
                                <p className="text-[12px] font-black text-[var(--text-primary)] uppercase tracking-tight">iOS</p>
                                <p className="text-2xs text-[var(--text-muted)]">{t('install_app.ios_subtitle')}</p>
                            </div>
                            <ExternalLink size={14} className="shrink-0 text-blue-400" />
                        </button>
                    )}

                    {/* PWA / Desktop */}
                    {!isNative && (
                        <button
                            type="button"
                            onClick={hasPwaPrompt && !pwaInstalled ? handlePwaInstall : undefined}
                            disabled={!hasPwaPrompt || pwaInstalled}
                            className="w-full bg-violet-500/5 hover:bg-violet-500/10 border border-violet-500/20 rounded-2xl p-4 flex items-center gap-3 transition-all active:scale-[0.98] disabled:opacity-60 disabled:cursor-default"
                        >
                            <div className="w-10 h-10 rounded-xl bg-violet-500/20 flex items-center justify-center text-violet-400 shrink-0">
                                <Monitor size={18} />
                            </div>
                            <div className="min-w-0 flex-1 text-left">
                                <p className="text-[12px] font-black text-[var(--text-primary)] uppercase tracking-tight">{t('install_app.pwa_title')}</p>
                                <p className="text-2xs text-[var(--text-muted)]">
                                    {pwaInstalled
                                        ? t('install_app.pwa_installed')
                                        : hasPwaPrompt
                                            ? t('install_app.pwa_subtitle')
                                            : t('install_app.pwa_already_installed')}
                                </p>
                            </div>
                            {hasPwaPrompt && !pwaInstalled ? (
                                <Download size={14} className="shrink-0 text-violet-400" />
                            ) : (
                                <CheckCircle2 size={14} className="shrink-0 text-emerald-400" />
                            )}
                        </button>
                    )}

                    {/* Info toggle */}
                    <button
                        onClick={() => setShowPwaInfo(p => !p)}
                        className="w-full flex items-center justify-center gap-2 pt-1"
                    >
                        <Info size={11} className="text-[var(--text-muted)]" />
                        <span className="text-3xs font-bold text-[var(--text-muted)] uppercase tracking-widest">{t('install_app.pwa_vs_native_title')}</span>
                        <ChevronDown size={11} className={`text-[var(--text-muted)] transition-transform ${showPwaInfo ? 'rotate-180' : ''}`} />
                    </button>
                    {showPwaInfo && (
                        <div className="bg-[var(--bg-input)] rounded-xl p-3 border border-[var(--border-primary)] animate-in fade-in slide-in-from-top-1 duration-200">
                            <p className="text-2xs text-[var(--text-secondary)] leading-relaxed">
                                {t('install_app.pwa_vs_native_desc')}
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default InstallAppModal;
